import { ReactElement, ReactNode, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import AuthenticationContext from "../shared/AuthenticationContext";
import ErrorContext, { ErrorProvider } from "../shared/ErrorContext";
import AuthenticationService, {
  ValidLogonResponse
} from "../../services/AuthenticationService";
import StaticHeader from "./components/StaticHeader";
import Loading from "../shared/components/Loading";
import logo from "../../assets/logo-default.png";
import Footer from "../shared/Footer";
import {
  doCompanyLogin,
  getQueryParams,
  routeByVersion
} from "../../utils/authHelpers";
import { TranslationService } from "../../services/TranslationService";
import { SafeHtml } from "../../components/SafeHtml";

function extractReturnUrl(search: string): string {
  const qs = (search.startsWith("?") ? search.slice(1) : search).replace(
    /&amp;/gi,
    "&"
  );
  let val = qs.includes("returnUrl") ? qs.split("returnUrl=")[1] : "";
  try {
    val = decodeURIComponent(val);
  } catch {}
  try {
    val = decodeURIComponent(val);
  } catch {}
  return val;
}

const BodyContainer = ({ children }: { children?: ReactNode }) => {
  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center flex-grow-1 py-5"
      style={{ backgroundColor: "#f2f1f6" }}
    >
      <img
        src={logo}
        alt="intiza"
        width="150"
        height="auto"
        className="d-none mb-3 mt-1"
      />
      {children}
    </div>
  );
};

const HomeErrorMessage = () => {
  const { currentError } = useContext(ErrorContext);
  return (
    <SafeHtml
      html={currentError ?? ""}
      className="incorrect"
      style={{
        color: "red",
        fontSize: "14px",
        textAlign: "center",
        margin: "0px 0px 10px"
      }}
    />
  );
};

function LogonAuthToken({ lang }: { lang: string }): ReactElement {
  const history = useHistory();
  const { doLogout, setCurrentAuthentication } = useContext(
    AuthenticationContext
  );
  const { currentLanguage } = TranslationService;
  const { token: loginToken, guid: gid } = getQueryParams(
    history.location.search
  );

  const returnUrl = extractReturnUrl(window.location.search);
  const doLogoutRef = useRef(doLogout);
  const setAuthRef = useRef(setCurrentAuthentication);
  const historyRef = useRef(history);
  const ranRef = useRef<string | null>(null);

  useEffect(() => {
    doLogoutRef.current = doLogout;
  }, [doLogout]);
  useEffect(() => {
    setAuthRef.current = setCurrentAuthentication;
  }, [setCurrentAuthentication]);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);
  useEffect(() => {
    if (!loginToken) {
      historyRef.current.replace(`/${lang}/logout`);
      return;
    }

    const runKey = `${loginToken}|${gid}|${returnUrl}`;

    if (ranRef.current === runKey) return;

    ranRef.current = runKey;

    let cancelled = false;

    (async () => {
      try {
        await doLogoutRef.current();

        const logonResponse: ValidLogonResponse = {
          response: "OK",
          token: loginToken,
          lastcompanyguid: gid
        };
        AuthenticationService.saveUserPayload(logonResponse);

        await doCompanyLogin({
          userData: logonResponse,
          remember: false,
          setCurrentAuthentication: setAuthRef.current
        });

        if (cancelled) return;

        await routeByVersion({
          history: historyRef.current,
          location: window.location,
          doLogout: doLogoutRef.current,
          currentLanguage: currentLanguage || lang,
          overrideReturnUrl: returnUrl || undefined,
          reloadOnInternal: true
        });
      } catch (e) {
        if (!cancelled) historyRef.current.replace(`/${lang}/logout`);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loginToken, gid, returnUrl, lang, currentLanguage]);

  return (
    <ErrorProvider>
      <div className="old-page">
        <StaticHeader lang={lang} />
        <BodyContainer>
          <div className="pt-5">
            <div className="container logon" id="signin">
              <HomeErrorMessage />
              <div className="d-flex flex-column justify-content-center">
                <Loading />
                <h3 className="ms-2 text-center">Ingresando</h3>
              </div>
            </div>
          </div>
        </BodyContainer>
        <Footer />
      </div>
    </ErrorProvider>
  );
}

export default LogonAuthToken;
